import React, { useCallback, useState } from "react";
import { FaFilePdf } from "react-icons/fa";
import "./index.css";
import { ImAttachment } from "react-icons/im";
import { TiDelete } from "react-icons/ti";
import { translate } from "../../../utils/Common";

const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const getFileTypeLabel = (file) => {
  if (!file) return null;
  if (file.type === "application/pdf") return "PDF";
  if (file.type === "image/png") return "PNG";
  if (file.type === "image/jpeg" || file.type === "image/jpg") return "JPG";
  const name = file.name || "";
  const ext = name.split(".").pop();
  return ext ? ext.toUpperCase() : null;
};

const AttachUploadsFiles = ({
  files,
  setFiles,
  onUploadToS3,
  onErrorChange,
  onShowErrorToast,
  onShowSuccessToast,
}) => {
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileUpload = useCallback(
    async (event) => {
      const newFiles = Array.from(event.target.files || []);
      const validFormats = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      const maxSize = 5 * 1024 * 1024; // 5 MB

      if (!newFiles.length) return;

      const invalidTypeFiles = newFiles.filter(
        (file) => !validFormats.includes(file.type)
      );
      const oversizedFiles = newFiles.filter((file) => file.size > maxSize);

      if (invalidTypeFiles.length || oversizedFiles.length) {
        const msgParts = [];
        if (invalidTypeFiles.length) {
          msgParts.push(translate("global.fileInvalidFormat"));
        }
        if (oversizedFiles.length) {
          msgParts.push(translate("global.fileMaxSize"));
        }
        const msg = msgParts.join(" ");
        setErrorMessage(msg);
        onErrorChange && onErrorChange(msg);
        onShowErrorToast && onShowErrorToast(msg);
      } else {
        setErrorMessage("");
        onErrorChange && onErrorChange("");
      }

      let filteredFiles = newFiles.filter(
        (file) => validFormats.includes(file.type) && file.size <= maxSize
      );

      if (!filteredFiles.length) {
        event.target.value = "";
        setErrorMessage("");
        onErrorChange && onErrorChange("");
        return;
      }

      if (files.length + filteredFiles.length > 5) {
        const msg = translate("global.fileMaxAttach");
        setErrorMessage(msg);
        onErrorChange && onErrorChange(msg);
        onShowErrorToast && onShowErrorToast(msg);
        event.target.value = "";
        return;
      }

      const uploadFile = async (file) => {
        const id = `${file.name}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2)}`;
        const fileObj = {
          id,
          file,
          preview: file.type.startsWith("image")
            ? URL.createObjectURL(file)
            : null,
          fileUrl: null,
          status: "uploading",
        };
        setFiles((prev) => [...prev, fileObj]);

        try {
          const { fileUrl } = await onUploadToS3(file);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id ? { ...f, fileUrl, status: "completed" } : f
            )
          );
          onShowSuccessToast &&
            onShowSuccessToast(translate("global.fileUploadSuccess"));
        } catch (err) {
          console.error(err);
          setFiles((prev) =>
            isLocalhost
              ? prev.filter((f) => f.id !== id)
              : prev.map((f) =>
                  f.id === id ? { ...f, status: "error" } : f
                )
          );
          const msg = translate("global.fileUploadError");
          setErrorMessage(msg);
          onErrorChange && onErrorChange(msg);
          onShowErrorToast && onShowErrorToast(msg);
        }
      };

      filteredFiles.forEach(uploadFile);
      event.target.value = "";
    },
    [files.length, onUploadToS3]
  );
  
  const removeFile = useCallback((id) => {
    setFiles((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, [setFiles]);

  return (
    <div className="file-uploader-container">
      <label className="upload-box">
        <input
          type="file"
          className="hidden-input"
          multiple
          onChange={handleFileUpload}
          accept=".png,.jpeg,.jpg,.pdf,image/png,image/jpeg,image/jpg,application/pdf"
        />
        <div className="upload-box-content">
          <ImAttachment className="upload-icon" />
          <div className="upload-text">{translate("global.attachfiles")}</div>
          <div className="upload-info">
            {translate("global.attachfiles.helper")}
          </div>
        </div>
      </label>
      <div className="file-list">
        {files.map((fileObj) => (
          <div key={fileObj.id} className="file-item">
            <div className="file-card-preview">
              {fileObj.preview ? (
                <img
                  src={fileObj.preview}
                  alt={fileObj.file?.name || "preview"}
                  className="file-preview-attach"
                />
              ) : (
                <div className="file-icon-wrapper">
                  <FaFilePdf className="file-preview-icon" />
                </div>
              )}

              {fileObj.status === "uploading" && (
                <div className="file-card-overlay">
                  <span className="file-card-overlay-label">
                    {translate("global.uploading")}
                  </span>
                </div>
              )}

              <button
                type="button"
                className="btn-delete-preview"
                onClick={() => removeFile(fileObj.id)}
                aria-label={translate("global.removeFile")}
              >
                <TiDelete />
              </button>
            </div>

            <div className="file-details">
              <div className="file-name" title={fileObj.file?.name}>
                {fileObj.file?.name}
              </div>
              <div className="file-meta">
                <span className="file-size">
                  {fileObj.file
                    ? `${(fileObj.file.size / (1024 * 1024)).toFixed(2)} MB`
                    : null}
                </span>
                {fileObj.file && (
                  <span className="file-type">
                    · {getFileTypeLabel(fileObj.file)}
                  </span>
                )}
                {fileObj.status !== "error" && (
                  <span
                    className={`file-status ${
                      fileObj.status === "uploading"
                        ? "file-status-uploading"
                        : "file-status-completed"
                    }`}
                  >
                    {fileObj.status === "uploading"
                      ? translate("global.uploading")
                      : translate("global.ready")}
                  </span>
                )}
              </div>
              {fileObj.status === "uploading" && (
                <div className="progress-bar-container">
                  <div className="progress-bar progress-bar-indeterminate" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttachUploadsFiles;
